import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GitCompare, Search } from "lucide-react";
import { api, fmtIDR, fmtDate, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const ROWS = [
  ["Lokasi", (e) => e.location || "-"],
  ["Tanggal", (e) => (e.start_date ? fmtDate(e.start_date) : "-")],
  ["Status", (e) => e.status],
  ["Barang Dibawa", (e) => e.metrics.brought],
  ["Terjual Bersih", (e) => e.metrics.sold_net],
  ["Sell-through", (e) => `${e.metrics.sell_through}%`],
  ["Transaksi", (e) => e.metrics.txn],
  ["Omzet Bersih", (e) => fmtIDR(e.metrics.net_omzet)],
  ["HPP Terjual", (e) => fmtIDR(e.metrics.hpp_sold)],
  ["Margin Kotor", (e) => fmtIDR(e.metrics.gross_margin)],
  ["Total Biaya", (e) => fmtIDR(e.metrics.total_expense)],
  ["Laba/Rugi", (e) => fmtIDR(e.metrics.profit_loss)],
  ["Biaya / Transaksi", (e) => fmtIDR(e.cost_per_txn)],
  ["AOV", (e) => fmtIDR(e.metrics.aov)],
];

export default function BazarCompare() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [sel, setSel] = useState([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/bazar/events").then((r) => setEvents(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  const filtered = useMemo(() => events.filter((e) => !q.trim() || [e.code, e.name, e.location].some((x) => (x || "").toLowerCase().includes(q.toLowerCase()))), [events, q]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const compare = async () => {
    if (sel.length < 2) return toast.error("Pilih minimal 2 event untuk dibandingkan");
    setLoading(true);
    try { const r = await api.get("/bazar/compare", { params: { ids: sel.join(",") } }); setData(r.data.events); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="bazar-compare-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/bazar")} className="text-cocoa" data-testid="bazar-compare-back"><ArrowLeft className="w-4 h-4 mr-1" />Kembali</Button>
          <PageHeader title="Riwayat & Perbandingan Event" subtitle="Bandingkan performa antar event bazar sebagai dasar perencanaan event berikutnya" />
        </div>
      </div>

      <Panel className="p-3">
        <div className="relative mb-2 max-w-sm"><Search className="w-4 h-4 absolute left-2 top-2.5 text-wmuted" /><Input className="pl-8 bg-white" placeholder="Cari event…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bazar-compare-search" /></div>
        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 max-h-64 overflow-y-auto">
          {filtered.map((e) => (
            <label key={e.id} className="flex items-center gap-2 p-2 rounded-lg border border-taupe/20 bg-white cursor-pointer hover:bg-ivory/40" data-testid={`bazar-compare-pick-${e.id}`}>
              <Checkbox checked={sel.includes(e.id)} onCheckedChange={() => toggle(e.id)} />
              <div className="min-w-0"><div className="text-sm font-medium text-cocoa truncate">{e.name}</div><div className="text-[11px] text-wmuted">{e.code} · {e.status}</div></div>
            </label>
          ))}
          {!filtered.length && <div className="text-sm text-wmuted p-3">Tidak ada event.</div>}
        </div>
        <div className="flex justify-end mt-3"><Button onClick={compare} disabled={loading} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="bazar-compare-run"><GitCompare className="w-4 h-4 mr-1" />Bandingkan ({sel.length})</Button></div>
      </Panel>

      {data && data.length > 0 && (
        <Panel className="p-0 overflow-hidden" data-testid="bazar-compare-result">
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="sticky left-0 bg-panel">Metrik</TableHead>{data.map((e) => <TableHead key={e.id} className="text-right min-w-[140px]">{e.name}<div className="text-[10px] font-normal text-wmuted">{e.code}</div></TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {ROWS.map(([label, fn]) => (
                <TableRow key={label}>
                  <TableCell className="font-medium text-cocoa sticky left-0 bg-panel">{label}</TableCell>
                  {data.map((e) => <TableCell key={e.id} className="text-right text-sm">{fn(e)}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        </Panel>
      )}
    </div>
  );
}
